import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  LoginRequest,
  LoginResponse,
  AuthData,
  SetPasswordRequest,
  SetPasswordResponse,
  VerifySetPasswordLinkRequest,
  VerifySetPasswordLinkResponse,
} from '../../interfaces/auth/auth';
import { Observable, finalize } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppStorage, getCookieOptions } from '@ui-lib';
import { API_ENDPOINTS } from '../../utils/api-endpoints.const';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieService = inject(CookieService);

  private readonly COOKIE_OPTIONS = getCookieOptions(this.platformId);

  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${API_ENDPOINTS.AUTH}/login`,
      request
    );
  }

  /**
   * Verify an account-claiming deep link before the set-password form renders.
   * A bad link returns `200 { valid: false }` rather than an error, so callers
   * must branch on `data.valid`, not on the HTTP status.
   */
  verifySetPasswordLink(
    request: VerifySetPasswordLinkRequest
  ): Observable<VerifySetPasswordLinkResponse> {
    return this.http.post<VerifySetPasswordLinkResponse>(
      `${API_ENDPOINTS.AUTH}/set-password/verify`,
      request
    );
  }

  /**
   * Claim a pre-registered investee account by setting its first password.
   * The account has no session yet, so this is a public call and returns a
   * generic success message (no auth token). The investee logs in afterwards.
   */
  setPassword(request: SetPasswordRequest): Observable<SetPasswordResponse> {
    return this.http.post<SetPasswordResponse>(
      `${API_ENDPOINTS.AUTH}/set-password`,
      request
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${API_ENDPOINTS.AUTH}/logout`, {}).pipe(
      finalize(() => {
        this.clearAuthData();
      })
    );
  }

  setToken(token: string) {
    this.cookieService.set(AppStorage.token, token, this.COOKIE_OPTIONS);
  }

  setAuthData(authData: AuthData) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(AppStorage.authData, JSON.stringify(authData));
    }
  }

  getToken(): string | null {
    const token = this.cookieService.get(AppStorage.token);
    return token || null;
  }

  getAuthData(): AuthData | null {
    if (isPlatformBrowser(this.platformId)) {
      const data = localStorage.getItem(AppStorage.authData);
      return data ? JSON.parse(data) : null;
    }
    return null;
  }

  /**
   * Clear all authentication data (token and user data) from cookies and localStorage
   */
  clearAuthData() {
    this.cookieService.delete(AppStorage.token, '/');
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(AppStorage.authData);
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}
